import ky from "ky";
import queryString from "query-string";

import { jwt } from "../utils/index.js";

const { REACT_APP_MAIN_SERVER_URL } = process.env;

const rootApi = ky.extend({
	timeout: false,
	prefixUrl: `${REACT_APP_MAIN_SERVER_URL}/api`,
	retry: {
		statusCodes: [401, 408, 413, 429, 502, 503, 504],
		limit: 2,
		methods: ["get", "post", "put", "head", "delete", "options", "trace"],
	},
	hooks: {
		beforeRequest: [({ headers }) => {
			headers.set("x-access-token", jwt.getToken());
		}],
		beforeRetry: [
			async ({ request: { method }, error }) => {
				if (error?.response?.status === 401) {
					const res = await rootApi.extend({ throwHttpErrors: false, retry: 0 }).get("api/refresh");
					if (res.status === 401) {
						jwt.destroyToken();
						window.location.href = "/";
					} else {
						const { token } = await res.json();
						jwt.setToken(token);
					}
				} else if (method === "POST") {
					throw error;
				}
			},
		],
		afterResponse: [
			(_req, _opts, res) => {
				const { status } = res;
				if (status === 500) {
					return new Response(JSON.stringify({ success: false }), { status: 200 });
				}

				if (status === 404) {
					window.location.href = "/";
				}

				return res;
			},
		],
	},
});

const api = {
	get: (path, searchParams) => rootApi.get(path, { searchParams: queryString.stringify(searchParams) }).json(),
	post: (path, json) => rootApi.post(path, { json }).json(),
	put: (path, json) => rootApi.put(path, { json }).json(),
	patch: (path, json) => rootApi.patch(path, { json }).json(),
	delete: (path, json) => rootApi.delete(path, { json }).json(),
};

export default api;

export const uploadFile = (body) => rootApi.post("file/", { body }).json();
export const reUploadFile = (body) => rootApi.put("file/", { body }).json();
export const deleteFile = (info) => api.post("file/delete/", info);
export const getUsersData = () => api.get("user");
export const getProfile = () => api.get("user/profile");
export const getTeam = () => api.get("team");
export const createTeam = (values) => api.post("team", values);
export const addMember = (values) => api.post("team/member", values);
export const leaveTeam = () => api.delete("team/member");
export const updateTeam = (values) => api.put("team", values);
export const getTeams = () => api.get("team/teams");
export const addMemberAdmin = (teamId, values) => api.post(`team/member/${teamId}`, values);
export const removeTeamAdmin = (teamId) => api.delete(`team/${teamId}`);
export const updateTeamAdmin = (teamId, values) => api.put(`team/${teamId}`, values);
export const removeMemberAdmin = (teamId, userId) => api.delete(`team/member/${teamId}/${userId}`);
export const createTeamAdmin = (values) => api.post("team/teams", values);
export const getTasksAdmin = () => api.get("task");
export const getTeamTasksAdmin = (teamId) => api.get(`task/team/${teamId}`);
export const getAvailableGoalDSLs = () => api.get("task/goalDSLModels");
export const getAvailableCategories = () => api.get("task/categories");
export const getAvailableSubcategories = () => api.get("task/subcategories");
export const createTask = (values) => api.post("task", values);
export const removeTaskAdmin = (taskId) => api.delete(`task/${taskId}`);
export const removeCategoryAdmin = (categoryName) => api.delete("task/task/category", { categoryName });
export const removeSubcategoryAdmin = (subcategoryName) => api.delete("task/task/subcategory", { subcategoryName });
export const updateCategoryAdmin = (categoryName, values) => api.put("task/category", { categoryName, ...values });
export const updateSubcategoryAdmin = (subcategoryName, values) => api.put("task/subcategory", { subcategoryName, ...values });
export const editTaskAdmin = (taskId, values) => api.put(`task/edit/${taskId}`, values);
export const updateCategoryDatesAdmin = (categoryName, values) => api.put("task/category/dates", { categoryName, ...values });
export const updateSubcategoryDatesAdmin = (subcategoryName, values) => api.put("task/subcategory/dates", { subcategoryName, ...values });
export const getTasks = () => api.get("task/tasks");
export const getScores = (taskId) => api.get(`score${taskId ? `/${taskId}` : ""}`);
export const getStatistics = () => api.get("general");
export const getSubmissions = () => api.get("submission");
export const createSubmission = (values) => api.post("submission", values);
export const editSubmission = (id, values) => api.put(`submission/${id}`, values);
export const deleteSubmission = (id) => api.delete(`submission/${id}`);
export const getSubmissionsAdmin = () => api.get("submission/submissions");
